import React, { useEffect, useState } from "react";
import { ExternalLink, Instagram } from "lucide-react";
import { buscarProximaEdicao } from "@/lib/edicoes";
import { registrarAcesso } from "@/lib/acessos";
import { Edicao } from "@/types/edicao";
import WaitlistModal from "@/components/landing/WaitlistModal";
import MapModal from "@/components/landing/MapModal";
import PlayerBar from "@/components/landing/PlayerBar";
import GameSection from "@/components/landing/GameSection";
import { useInView } from "@/hooks/useInView";

const PARCEIROS = [
  "/images/parceiros/logo-1.png",
  "/images/parceiros/logo-2.png",
  "/images/parceiros/logo-3.png",
  "/images/parceiros/logo-4.png",
  "/images/parceiros/logo-5.png",
];

function mapsToEmbedUrl(url: string): string {
  const coord = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+),(\d+)/);
  if (coord) {
    return `https://www.google.com/maps?q=${coord[1]},${coord[2]}&z=${coord[3]}&output=embed`;
  }
  const q = url.match(/[?&]q=([^&]+)/);
  if (q) return `https://maps.google.com/maps?q=${q[1]}&output=embed`;
  if (url.includes("output=embed")) return url;
  return url + (url.includes("?") ? "&" : "?") + "output=embed";
}

function calcularDias(dataStr: string): number {
  const [ano, mes, dia] = dataStr.split("-").map(Number);
  const hoje = new Date();
  const hojeLocal = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
  const data = new Date(ano, mes - 1, dia);
  return Math.round((data.getTime() - hojeLocal.getTime()) / (1000 * 60 * 60 * 24));
}

function formatarDataCurta(dataStr: string): string {
  const data = new Date(dataStr + "T12:00:00");
  return data.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
  });
}

export default function LandingPage() {
  const [proximaEdicao, setProximaEdicao] = useState<Edicao | null>(null);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [heroVisible, setHeroVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [scrolled, setScrolled] = useState(false);

  const secaoEdicao = useInView(0.1);
  const secaoFooter = useInView(0.2);

  useEffect(() => {
    const t = setTimeout(() => setHeroVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    buscarProximaEdicao()
      .then((edicao) => {
        setProximaEdicao(edicao);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, []);

  // Registrar acesso
  useEffect(() => {
    const registrar = async () => {
      try {
        // Busca geolocalização por IP
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        
        await registrarAcesso({
          cidade: data.city || "Desconhecida",
          estado: data.region || "",
          pais: data.country_name || "Brasil",
          ip: data.ip || "",
          userAgent: navigator.userAgent,
          timestamp: new Date(),
        });
      } catch {
        // Registra mesmo sem geolocalização
        try {
          await registrarAcesso({
            cidade: "Desconhecida",
            estado: "",
            pais: "Brasil",
            ip: "",
            userAgent: navigator.userAgent,
            timestamp: new Date(),
          });
        } catch {
          // Falha silenciosa — registro de acesso não é crítico
        }
      }
    };
    
    registrar();
  }, []);

  const dias = proximaEdicao ? calcularDias(proximaEdicao.data) : null;
  const cidadeMarquee = proximaEdicao?.cidade ?? "EM BREVE";
  const diasTexto =
    dias === null
      ? "EM BREVE"
      : dias === 0
      ? "HOJE"
      : dias < 0
      ? "CONFIRA A PRÓXIMA"
      : `FALTAM ${dias} DIA${dias !== 1 ? "S" : ""}`;

  const marqueeItem = `${cidadeMarquee.toUpperCase()} • ${diasTexto} `;
  const marqueeContent = marqueeItem.repeat(12);

  return (
    <div className="bg-black text-white overflow-x-hidden" style={{ fontFamily: 'var(--font-oswald)' }}>
      {/* LOADING SCREEN */}
      {isLoading && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center transition-opacity duration-500"
          style={{
            backgroundColor: "#DA422F",
          }}
        >
          <div
            className="animate-spin rounded-full border-8"
            style={{
              width: "200px",
              height: "200px",
              borderColor: "#F8EBC1",
              borderTopColor: "transparent",
            }}
          />
        </div>
      )}

      {/* HERO */}
      <section
        className="noise relative h-[70vh] md:h-screen overflow-hidden"
        style={{ backgroundColor: "#DA422F" }}
      >
        <img
          src="/images/foto-hero-huff-3.png"
          alt=""
          className="!absolute inset-0 z-0 w-full h-full object-cover object-top"
        />
      </section>

      {/* WRAPPER relativo */}
      <div className="relative" style={{ zIndex: 0, isolation: "isolate" }}>
        {/* MARQUEE */}
        <div className="noise overflow-hidden py-4" style={{ backgroundColor: "#36a9c8" }}>
          <div className="flex whitespace-nowrap animate-marquee">
            <span className="text-white text-2xl md:text-3xl font-bold uppercase tracking-wider">
              {marqueeContent}
            </span>
            <span className="text-white text-2xl md:text-3xl font-bold uppercase tracking-wider" aria-hidden>
              {marqueeContent}
            </span>
          </div>
        </div>

        {/* VIDEO PARALLAX */}
        <section className="relative h-[60vh] md:h-[70vh] w-full overflow-hidden bg-black">
          <div
            id="yt-player-main"
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none opacity-100"
            style={{
              width: "calc(100% + 200px)",
              height: "calc(100% + 200px)",
              minWidth: "177.78vh",
              minHeight: "56.25vw",
            }}
          />
          <div 
            className="absolute inset-0 bg-cover bg-center pointer-events-none" 
            style={{ backgroundImage: "url(/images/overlay-video.png)" }}
          />
        </section>

        {/* PRÓXIMA EDIÇÃO */}
        <section
          ref={secaoEdicao.ref as React.RefObject<HTMLElement>}
          className="noise px-6 py-24 md:py-32 relative"
          style={{ 
            backgroundColor: "#DA422F",
            backgroundImage: "url(/images/bg-evento.png)",
            backgroundSize: "cover",
            backgroundPosition: "center top"
          }}
        >
          <div className="max-w-3xl mx-auto space-y-10 text-center">
            <p
              className={`text-xs tracking-[0.4em] uppercase anim-hidden ${secaoEdicao.inView ? "anim-fade-down" : ""}`}
              style={{ color: "#F8EBC1" }}
            >
              Próxima Edição
            </p>

            {proximaEdicao ? (
              <>
                {/* 1. Data e Cidade */}
                <div className="space-y-3">
                  <h2
                    className={`text-5xl md:text-7xl lg:text-8xl font-bold uppercase leading-none tracking-tight anim-hidden flex items-center justify-center gap-3 md:gap-5 ${secaoEdicao.inView ? "anim-fade-up anim-delay-1" : ""}`}
                    style={{ color: "#F8EBC1" }}
                  >
                    <span>{formatarDataCurta(proximaEdicao.data)}</span>
                    <img src="/images/separador.png" alt="" className="w-8 h-8 md:w-12 md:h-12 object-contain translate-y-[5px]" />
                    <span>{proximaEdicao.cidade.toUpperCase()}</span>
                  </h2>

                  {/* 2. Local */}
                  <p
                    className={`text-3xl md:text-4xl lg:text-5xl uppercase tracking-wide font-light anim-hidden ${secaoEdicao.inView ? "anim-fade-up anim-delay-2" : ""}`}
                    style={{ color: "#F8EBC1" }}
                  >
                    {proximaEdicao.nomeLocal.toUpperCase()}
                  </p>
                </div>

                {/* 3. Mapa de Áreas */}
                {proximaEdicao.fotoMapa && (
                  <div className={`anim-hidden ${secaoEdicao.inView ? "anim-fade-up anim-delay-3" : ""}`}>
                    <div className="relative w-full h-[280px] md:h-[360px] overflow-hidden rounded-lg">
                      <img
                        src={proximaEdicao.fotoMapa}
                        alt={`Mapa do evento - ${proximaEdicao.cidade}`}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      <button
                        onClick={() => setMapModalOpen(true)}
                        className="absolute bottom-3 right-3 px-4 py-2 text-xs font-bold uppercase tracking-widest transition shadow-lg hover:opacity-90 rounded-full"
                        style={{ backgroundColor: "#F8EBC1", color: "#DA422F" }}
                      >
                        Ver em Tela Cheia
                      </button>
                    </div>
                  </div>
                )}

                <div
                  className={`h-px anim-hidden ${secaoEdicao.inView ? "anim-fade-in anim-delay-4" : ""}`}
                  style={{ backgroundColor: "#F8EBC133" }}
                />

                {/* 4. Atrações */}
                <div className="space-y-2">
                  {proximaEdicao.atracoes.map((atracao, i) => (
                    <p
                      key={i}
                      className={`text-2xl md:text-3xl font-semibold uppercase tracking-wide anim-hidden ${secaoEdicao.inView ? `anim-fade-up anim-delay-${Math.min(i + 4, 6)}` : ""}`}
                      style={{ color: "#F8EBC1" }}
                    >
                      {atracao}
                    </p>
                  ))}
                </div>

                {/* 5. Botões */}
                <div className={`flex flex-wrap justify-center gap-3 pt-2 anim-hidden ${secaoEdicao.inView ? "anim-fade-up anim-delay-6" : ""}`}>
                  {proximaEdicao.linkIngressos && (
                    <a
                      href={proximaEdicao.linkIngressos}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center px-8 py-4 text-base font-bold tracking-[0.2em] uppercase transition hover:opacity-80 rounded-full"
                      style={{ backgroundColor: "#F8EBC1", color: "#DA422F" }}
                    >
                      Compre seu Ingresso
                    </a>
                  )}

                  {proximaEdicao.listaEspera && (
                    <button
                      onClick={() => setWaitlistOpen(true)}
                      className="flex items-center justify-center px-8 py-4 text-base font-bold tracking-[0.2em] uppercase border transition hover:opacity-80 rounded-full"
                      style={{ borderColor: "#F8EBC166", color: "#F8EBC1" }}
                    >
                      Lista de Espera
                    </button>
                  )}
                </div>
              </>
            ) : (
              <p
                className={`text-4xl md:text-6xl font-bold uppercase anim-hidden ${secaoEdicao.inView ? "anim-fade-up anim-delay-1" : ""}`}
                style={{ color: "#F8EBC133" }}
              >
                Em breve
              </p>
            )}
          </div>
        </section>

        {/* GOOGLE MAPS */}
        {proximaEdicao?.linkMaps && (
          <section className={`relative w-full anim-hidden ${secaoEdicao.inView ? "anim-fade-up anim-delay-7" : ""}`}>
            <div className="relative w-full h-[480px] md:h-[560px] overflow-hidden">
              <iframe
                src={mapsToEmbedUrl(proximaEdicao.linkMaps)}
                width="100%"
                height="100%"
                style={{ border: 0, pointerEvents: "none" }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Localização do evento"
              />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <img
                  src="/images/map.png"
                  alt="Marcador do local"
                  className="w-20 h-20 object-contain -translate-y-6"
                />
              </div>
              <a
                href={proximaEdicao.linkMaps}
                target="_blank"
                rel="noopener noreferrer"
                className="absolute bottom-4 right-4 flex items-center gap-1.5 px-4 py-2 bg-black text-white text-xs font-bold uppercase tracking-widest hover:bg-black/80 transition shadow-lg"
              >
                Abrir no Maps
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </section>
        )}
      </div>

      {/* DU NADA GAME */}
      <GameSection />

      {/* FOOTER */}
      <footer
        ref={secaoFooter.ref as React.RefObject<HTMLElement>}
        className="px-8 py-14 pb-28"
        style={{ backgroundColor: "#F8EBC1" }}
      >
        <div className={`max-w-6xl mx-auto space-y-8 anim-hidden ${secaoFooter.inView ? "anim-fade-up" : ""}`}>
          {/* Logo DU NADA e botão Instagram */}
          <div className="flex flex-col items-center gap-4">
            <img
              src="/images/dunada-preto.png"
              alt="DU NADA"
              className="object-contain"
              style={{ width: "250px" }}
            />
            <a
              href="https://instagram.com/dunadamurilohuff"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-medium uppercase transition hover:bg-black/5"
              style={{ border: "2px solid #3A2824", color: "#3A2824" }}
            >
              <Instagram className="w-4 h-4" />
              @dunadamurilohuff
            </a>
          </div>

          {/* Linha divisória */}
          <div className="py-4">
            <div className="w-full h-px bg-black/10" />
          </div>

          {/* Logos laterais */}
          <div className="flex items-center justify-center gap-8">
            <img
              src="/images/logo-artinvox.png"
              alt="Artinvox"
              className="object-contain"
              style={{ width: "70px" }}
            />
            <img
              src="/images/roc-estudio-logo.png"
              alt="ROC"
              className="object-contain"
              style={{ width: "70px" }}
            />
          </div>

          {/* Texto Copyright */}
          <div className="text-center">
            <p className="text-black/30 text-xs" style={{ fontFamily: "Roboto, sans-serif" }}>
              Desenvolvido por Mídia Para Artistas
            </p>
          </div>
        </div>
      </footer>

      {/* MODAL LISTA DE ESPERA */}
      {waitlistOpen && proximaEdicao && (
        <WaitlistModal
          edicaoId={proximaEdicao.id}
          edicaoNome={`${proximaEdicao.cidade} — ${proximaEdicao.nomeLocal}`}
          cidade={proximaEdicao.cidade}
          onClose={() => setWaitlistOpen(false)}
        />
      )}

      {/* MODAL DO MAPA */}
      {mapModalOpen && proximaEdicao?.fotoMapa && (
        <MapModal
          fotoMapa={proximaEdicao.fotoMapa}
          cidade={proximaEdicao.cidade}
          nomeLocal={proximaEdicao.nomeLocal}
          onClose={() => setMapModalOpen(false)}
        />
      )}

      {/* BOTÃO FIXO (substitui o player) */}
      {proximaEdicao?.linkIngressos && (
        <div
          className="noise fixed bottom-0 left-0 right-0 z-50 border-t"
          style={{
            backgroundColor: "#DA422F",
            borderColor: "#F8EBC133",
            position: "fixed",
          }}
        >
          <div className="max-w-6xl mx-auto px-8 h-20 flex items-center justify-center">
            <a
              href={proximaEdicao.linkIngressos}
              target="_blank"
              rel="noopener noreferrer"
              className="anim-pulse-soft flex items-center justify-center px-8 py-4 text-base font-bold tracking-[0.2em] uppercase transition hover:opacity-80 rounded-full"
              style={{ backgroundColor: "#F8EBC1", color: "#DA422F" }}
            >
              Compre seu Ingresso
            </a>
          </div>
        </div>
      )}

      {/* PLAYER DE MÚSICA (controles ocultos — vídeo continua na seção parallax) */}
      <PlayerBar containerId="yt-player-main" hideControls />

      {/* FLOATING AVATAR CTA */}
      <a
        href="/avatar"
        className="group fixed bottom-24 right-4 z-50 flex flex-col items-end gap-3 transition-all duration-300"
        style={{ textDecoration: "none" }}
      >
        {/* Balão de chat */}
        <div
          className="relative transition-all duration-300"
          style={{
            backgroundColor: "#F8EBC1",
            color: "#DA422F",
            fontFamily: "var(--font-oswald)",
            fontWeight: 700,
            fontSize: "0.8rem",
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            padding: "8px 16px",
            borderRadius: "14px 14px 4px 14px",
            whiteSpace: "nowrap",
            boxShadow: "0 4px 20px rgba(0,0,0,0.22)",
            opacity: scrolled ? 0 : 1,
            transform: scrolled ? "translateY(6px) scale(0.9)" : "translateY(0) scale(1)",
            pointerEvents: scrolled ? "none" : "auto",
          }}
        >
          Faça seu avatar personalizado!
          <span
            style={{
              position: "absolute",
              bottom: "-8px",
              right: "24px",
              width: 0,
              height: 0,
              borderLeft: "8px solid transparent",
              borderRight: "0px solid transparent",
              borderTop: "9px solid #F8EBC1",
            }}
          />
        </div>

        {/* Avatar flutuante */}
        <div
          className="anim-pulse-soft rounded-full overflow-hidden transition-all duration-300 group-active:scale-95 self-end"
          style={{
            width: scrolled ? "52px" : "80px",
            height: scrolled ? "52px" : "80px",
            boxShadow: "0 6px 28px rgba(0,0,0,0.35)",
          }}
        >
          <img
            src="/images/exemplo.jpg"
            alt="Faça seu avatar"
            className="w-full h-full object-cover"
          />
        </div>
      </a>
    </div>
  );
}
